/**
 * ReviewDebugView — collapsible JSON viewers for review context and documents.
 */

import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faCode,
  faFile,
  faRefresh,
  faArrowLeft,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge/Badge";
import type { ProgenyReviewDocuments } from "@/services/api/progeny";

interface ReviewDebugViewProps {
  reviewId: string;
  context: Record<string, unknown> | null;
  documents: ProgenyReviewDocuments | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onBack: () => void;
}

function CollapsibleJSON({
  title,
  icon,
  data,
  defaultOpen,
  testId,
}: {
  title: string;
  icon: typeof faCode;
  data: unknown;
  defaultOpen?: boolean;
  testId: string;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <div
      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
      data-testid={testId}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className="text-slate-400 text-xs"
        />
        <FontAwesomeIcon icon={icon} className="text-slate-400" />
        <span className="font-medium text-slate-900 dark:text-white">
          {title}
        </span>
        {data == null && (
          <Badge color="zinc" size="sm">No data</Badge>
        )}
      </button>
      {open && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-4">
          {data != null ? (
            <pre className="text-xs font-mono text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 rounded-lg p-4 overflow-auto max-h-[500px] whitespace-pre-wrap">
              {JSON.stringify(data, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500">
              No data available for this review.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Two collapsible JSON viewers for review context and documents.
 * Useful for debugging what Progeny returns for a given review.
 */
export function ReviewDebugView({
  reviewId,
  context,
  documents,
  loading,
  error,
  onRefresh,
  onBack,
}: ReviewDebugViewProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96" data-testid="progeny-review-loading">
        <Spinner size="lg" label={`Loading review ${reviewId}...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 p-6" data-testid="progeny-review-error">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
        <div className="flex gap-2">
          <Button outline onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </Button>
          <Button outline onClick={onRefresh}>
            <FontAwesomeIcon icon={faRefresh} />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="progeny-review-debug">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button outline onClick={onBack} data-testid="progeny-review-back">
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </Button>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Review Debug
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">
              {reviewId}
            </p>
          </div>
        </div>
        <Button outline onClick={onRefresh} data-testid="progeny-review-refresh">
          <FontAwesomeIcon icon={faRefresh} />
          Refresh
        </Button>
      </div>

      {documents && (
        <div className="flex items-center gap-3 text-sm">
          <Badge color="blue">Client: {documents.client_id}</Badge>
          <Badge color="zinc">{documents.document_count} documents</Badge>
        </div>
      )}

      <CollapsibleJSON
        title="Review Context"
        icon={faCode}
        data={context}
        defaultOpen
        testId="progeny-review-context"
      />

      <CollapsibleJSON
        title="Documents"
        icon={faFile}
        data={documents?.documents ?? null}
        testId="progeny-review-documents"
      />
    </div>
  );
}
